import React, { useState, useEffect, useRef, useCallback } from 'react';
import Map, { Source, Layer, Marker, NavigationControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useTheme } from '@/contexts/ThemeContext';
import { 
  Shield, AlertTriangle, Globe, Activity, TrendingUp, TrendingDown,
  Server, Zap, Clock, RefreshCw, Target, Users, Bug, Eye, Database,
  ChevronDown, ChevronUp, Download, Maximize2, Minimize2, Settings,
  Palette, Sun, Moon, Grip, LayoutGrid
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

// OpenCTI-style dark map style — uses OpenStreetMap standard tiles (no API
// key required). The dark theme is achieved via a CSS filter applied on the
// map canvas in dark mode instead of using CARTO's key-gated dark_all tiles.
const OSM_TILE_URLS = [
  'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
  'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
  'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
];

const MAP_STYLE = {
  version: 8,
  name: 'Filigran Dark',
  sources: {
    'osm-tiles': {
      type: 'raster',
      tiles: OSM_TILE_URLS,
      tileSize: 256,
      attribution: '© OpenStreetMap contributors'
    }
  },
  layers: [
    {
      id: 'osm-tiles-layer',
      type: 'raster',
      source: 'osm-tiles',
      minzoom: 0,
      maxzoom: 19
    }
  ]
};

// Light map style
const MAP_STYLE_LIGHT = {
  version: 8,
  name: 'OSM Light',
  sources: {
    'osm-tiles': {
      type: 'raster',
      tiles: OSM_TILE_URLS,
      tileSize: 256,
      attribution: '© OpenStreetMap contributors'
    }
  },
  layers: [
    {
      id: 'osm-tiles-layer',
      type: 'raster',
      source: 'osm-tiles',
      minzoom: 0,
      maxzoom: 19
    }
  ]
};

// Country coordinates for attack visualization
const COUNTRY_COORDS = {
  'US': { lat: 39.8283, lng: -98.5795, name: 'United States' },
  'DE': { lat: 51.1657, lng: 10.4515, name: 'Germany' },
  'GB': { lat: 55.3781, lng: -3.4360, name: 'United Kingdom' },
  'FR': { lat: 46.2276, lng: 2.2137, name: 'France' },
  'NL': { lat: 52.1326, lng: 5.2913, name: 'Netherlands' },
  'RU': { lat: 61.5240, lng: 105.3188, name: 'Russia' },
  'CN': { lat: 35.8617, lng: 104.1954, name: 'China' },
  'JP': { lat: 36.2048, lng: 138.2529, name: 'Japan' },
  'BR': { lat: -14.2350, lng: -51.9253, name: 'Brazil' },
  'IN': { lat: 20.5937, lng: 78.9629, name: 'India' },
  'AU': { lat: -25.2744, lng: 133.7751, name: 'Australia' },
  'CA': { lat: 56.1304, lng: -106.3468, name: 'Canada' },
  'IT': { lat: 41.8719, lng: 12.5674, name: 'Italy' },
  'ES': { lat: 40.4637, lng: -3.7492, name: 'Spain' },
  'KR': { lat: 35.9078, lng: 127.7669, name: 'South Korea' },
  'PL': { lat: 51.9194, lng: 19.1451, name: 'Poland' },
  'UA': { lat: 48.3794, lng: 31.1656, name: 'Ukraine' },
  'TR': { lat: 38.9637, lng: 35.2433, name: 'Turkey' },
  'SE': { lat: 60.1282, lng: 18.6435, name: 'Sweden' },
  'NO': { lat: 60.4720, lng: 8.4689, name: 'Norway' },
  'FI': { lat: 61.9241, lng: 25.7482, name: 'Finland' },
  'SG': { lat: 1.3521, lng: 103.8198, name: 'Singapore' },
};

// Generate threat data, enhanced with live feeds when available
const generateThreatData = (liveData) => {
  const liveSummary = liveData?.summary || {};
  const liveFeeds = liveData?.feeds || {};
  const topMalware = liveData?.top_malware || [];
  const recentIocs = liveData?.recent_iocs || {};

  const threatActors = [
    { id: 'APT29', name: 'APT29 (Cozy Bear)', origin: 'Russia', attacks: Math.floor(Math.random() * 500) + 200 },
    { id: 'APT41', name: 'APT41 (Double Dragon)', origin: 'China', attacks: Math.floor(Math.random() * 400) + 150 },
    { id: 'Lazarus', name: 'Lazarus Group', origin: 'North Korea', attacks: Math.floor(Math.random() * 350) + 100 },
    { id: 'APT28', name: 'APT28 (Fancy Bear)', origin: 'Russia', attacks: Math.floor(Math.random() * 300) + 100 },
    { id: 'Sandworm', name: 'Sandworm Team', origin: 'Russia', attacks: Math.floor(Math.random() * 250) + 80 },
    { id: 'APT38', name: 'APT38', origin: 'North Korea', attacks: Math.floor(Math.random() * 200) + 60 },
  ];

  const intrusionSets = [
    { name: 'FIN7', count: Math.floor(Math.random() * 150) + 50 },
    { name: 'Carbanak', count: Math.floor(Math.random() * 120) + 40 },
    { name: 'TA505', count: Math.floor(Math.random() * 100) + 30 },
    { name: 'MuddyWater', count: Math.floor(Math.random() * 90) + 25 },
    { name: 'OilRig', count: Math.floor(Math.random() * 80) + 20 },
  ];

  // Use live malware data if available
  const malwareColors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#6b7280'];
  const malware = topMalware.length > 0
    ? topMalware.slice(0, 6).map((m, i) => ({
        name: m.name, percentage: Math.max(5, 40 - i * 7), color: malwareColors[i] || '#6b7280'
      }))
    : [
        { name: 'TrickBot', percentage: 34.5, color: '#ef4444' },
        { name: 'Emotet', percentage: 22.3, color: '#f97316' },
        { name: 'Cobalt Strike', percentage: 15.8, color: '#eab308' },
        { name: 'Mimikatz', percentage: 12.4, color: '#22c55e' },
        { name: 'Metasploit', percentage: 8.7, color: '#3b82f6' },
        { name: 'Others', percentage: 6.3, color: '#6b7280' },
      ];

  const vulnerabilities = [
    { cve: 'CVE-2021-44228', name: 'Log4Shell', count: Math.floor(Math.random() * 300) + 150 },
    { cve: 'CVE-2017-11882', name: 'Office Equation Editor', count: Math.floor(Math.random() * 250) + 100 },
    { cve: 'CVE-2021-34527', name: 'PrintNightmare', count: Math.floor(Math.random() * 200) + 80 },
    { cve: 'CVE-2020-1472', name: 'Zerologon', count: Math.floor(Math.random() * 180) + 70 },
    { cve: 'CVE-2019-19781', name: 'Citrix ADC', count: Math.floor(Math.random() * 150) + 50 },
    { cve: 'CVE-2021-26855', name: 'ProxyLogon', count: Math.floor(Math.random() * 140) + 45 },
  ];

  const activeTools = [
    { name: 'PowerSploit', percentage: 18.5, color: '#ef4444' },
    { name: 'CobaltStrike', percentage: 16.2, color: '#f97316' },
    { name: 'Mimikatz', percentage: 14.8, color: '#eab308' },
    { name: 'LCG Kit', percentage: 12.3, color: '#22c55e' },
    { name: 'TOR', percentage: 10.7, color: '#3b82f6' },
    { name: 'Royal Road', percentage: 8.5, color: '#8b5cf6' },
    { name: 'RMS', percentage: 6.4, color: '#ec4899' },
    { name: 'Others', percentage: 12.6, color: '#6b7280' },
  ];

  const targetedCountries = Object.entries(COUNTRY_COORDS).map(([code, data]) => ({
    code,
    name: data.name,
    lat: data.lat,
    lng: data.lng,
    attacks: Math.floor(Math.random() * 2000) + 100,
    trend: Math.random() > 0.5 ? 'up' : 'down'
  })).sort((a, b) => b.attacks - a.attacks);

  const targetedSectors = [
    { name: 'Manufacturing', count: Math.floor(Math.random() * 1500) + 800 },
    { name: 'Government & Administration', count: Math.floor(Math.random() * 1200) + 600 },
    { name: 'Finance', count: Math.floor(Math.random() * 1100) + 500 },
    { name: 'Technology', count: Math.floor(Math.random() * 1000) + 400 },
    { name: 'Telecommunications', count: Math.floor(Math.random() * 800) + 300 },
    { name: 'Healthcare', count: Math.floor(Math.random() * 700) + 250 },
    { name: 'Energy', count: Math.floor(Math.random() * 600) + 200 },
    { name: 'Transport', count: Math.floor(Math.random() * 500) + 150 },
  ];

  const targetedRegions = [
    { name: 'Europe', count: Math.floor(Math.random() * 3000) + 1500 },
    { name: 'North America', count: Math.floor(Math.random() * 2500) + 1200 },
    { name: 'Asia', count: Math.floor(Math.random() * 2000) + 1000 },
    { name: 'South America', count: Math.floor(Math.random() * 800) + 300 },
    { name: 'Africa', count: Math.floor(Math.random() * 500) + 150 },
  ];

  const latestCampaigns = [
    { name: 'FATA MORGANA', date: '2026-02-24', target: 'Shipping & Logistics', severity: 'critical' },
    { name: 'Operation Blacksmith', date: '2026-02-23', target: 'Defense Contractors', severity: 'high' },
    { name: 'Volt Typhoon', date: '2026-02-22', target: 'Critical Infrastructure', severity: 'critical' },
    { name: 'SolarWinds 2.0', date: '2026-02-21', target: 'Government Agencies', severity: 'high' },
  ];

  const indicatorSources = [
    { name: 'sekoia', count: Math.floor(Math.random() * 200000) + 150000 },
    { name: 'riskiq', count: Math.floor(Math.random() * 100000) + 80000 },
    { name: 'alienvault', count: Math.floor(Math.random() * 70000) + 50000 },
    { name: 'virustotal', count: Math.floor(Math.random() * 50000) + 30000 },
  ];

  return {
    stats: {
      threatActors: threatActors.length + Math.floor(Math.random() * 10),
      intrusionSets: 903 + Math.floor(Math.random() * 100),
      campaigns: 750 + Math.floor(Math.random() * 50),
      malware: liveSummary.unique_malware_families || (6620 + Math.floor(Math.random() * 500)),
      indicators: liveSummary.total_iocs || (430620 + Math.floor(Math.random() * 10000)),
      observables: 460650 + Math.floor(Math.random() * 20000),
    },
    trends: {
      threatActors: Math.floor(Math.random() * 20) - 5,
      intrusionSets: Math.floor(Math.random() * 400) - 50,
      campaigns: Math.floor(Math.random() * 300) - 30,
      malware: Math.floor(Math.random() * 3000) - 500,
      indicators: Math.floor(Math.random() * 400000) - 50000,
      observables: Math.floor(Math.random() * 350000) - 40000,
    },
    threatActors,
    intrusionSets,
    malware,
    vulnerabilities,
    activeTools,
    targetedCountries,
    targetedSectors,
    targetedRegions,
    latestCampaigns,
    indicatorSources,
    liveFeeds: {
      urlhaus: recentIocs.urlhaus || [],
      threatfox: recentIocs.threatfox || [],
      botnet_c2: recentIocs.botnet_c2 || [],
      malicious_urls: recentIocs.malicious_urls || [],
      feedStatus: liveFeeds,
      lastUpdated: liveSummary.last_updated || null,
    },
  };
};

// Donut Chart Component
const DonutChart = ({ data, title, size = 180 }) => {
  const total = data.reduce((sum, item) => sum + item.percentage, 0);
  let cumulative = 0;
  
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox="0 0 100 100">
        {data.map((item, idx) => {
          const startAngle = (cumulative / total) * 360 - 90;
          cumulative += item.percentage;
          const endAngle = (cumulative / total) * 360 - 90;
          
          const startRad = (startAngle * Math.PI) / 180;
          const endRad = (endAngle * Math.PI) / 180;
          
          const x1 = 50 + 40 * Math.cos(startRad);
          const y1 = 50 + 40 * Math.sin(startRad);
          const x2 = 50 + 40 * Math.cos(endRad);
          const y2 = 50 + 40 * Math.sin(endRad);
          
          const largeArc = item.percentage / total > 0.5 ? 1 : 0;
          
          return (
            <path
              key={idx}
              d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
              fill={item.color}
              stroke="currentColor"
              strokeWidth="1"
              strokeOpacity="0.2"
              className="hover:opacity-80 transition-opacity cursor-pointer"
            />
          );
        })}
        <circle cx="50" cy="50" r="25" className="fill-background" />
      </svg>
      <div className="flex flex-wrap justify-center gap-2 mt-3 max-w-[200px]">
        {data.slice(0, 6).map((item, idx) => (
          <div key={idx} className="flex items-center gap-1 text-xs">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
            <span className="text-muted-foreground">{item.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Resizable Card Component
const ResizableCard = ({ children, title, icon: Icon, defaultHeight = 300, minHeight = 150, maxHeight = 600, onExport }) => {
  const [height, setHeight] = useState(defaultHeight);
  const [isResizing, setIsResizing] = useState(false);
  const cardRef = useRef(null);
  
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };
  
  useEffect(() => {
    if (!isResizing) return;
    
    const handleMouseMove = (e) => {
      if (!cardRef.current) return;
      const rect = cardRef.current.getBoundingClientRect();
      const newHeight = Math.max(minHeight, Math.min(maxHeight, e.clientY - rect.top));
      setHeight(newHeight);
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, minHeight, maxHeight]);
  
  return (
    <Card ref={cardRef} className="overflow-hidden relative" style={{ height }}>
      <CardHeader className="py-2 px-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4 text-primary" />}
            <CardTitle className="text-sm font-semibold">{title}</CardTitle>
          </div>
          <div className="flex items-center gap-1">
            {onExport && (
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onExport}>
                <Download className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-3 pt-0 overflow-auto" style={{ height: height - 50 }}>
        {children}
      </CardContent>
      {/* Resize handle */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize flex items-center justify-center bg-muted/50 hover:bg-muted transition-colors"
        onMouseDown={handleMouseDown}
      >
        <Grip className="w-4 h-4 text-muted-foreground rotate-90" />
      </div>
    </Card>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, trend, icon: Icon, color = 'cyan' }) => {
  const { isDark } = useTheme();
  
  const colorClasses = {
    cyan: isDark ? 'text-cyan-400 border-cyan-400/30 bg-cyan-400/10' : 'text-cyan-600 border-cyan-200 bg-cyan-50',
    orange: isDark ? 'text-orange-400 border-orange-400/30 bg-orange-400/10' : 'text-orange-600 border-orange-200 bg-orange-50',
    green: isDark ? 'text-green-400 border-green-400/30 bg-green-400/10' : 'text-green-600 border-green-200 bg-green-50',
    purple: isDark ? 'text-purple-400 border-purple-400/30 bg-purple-400/10' : 'text-purple-600 border-purple-200 bg-purple-50',
    pink: isDark ? 'text-pink-400 border-pink-400/30 bg-pink-400/10' : 'text-pink-600 border-pink-200 bg-pink-50',
    yellow: isDark ? 'text-yellow-400 border-yellow-400/30 bg-yellow-400/10' : 'text-yellow-600 border-yellow-200 bg-yellow-50',
  };
  
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(2)}K`;
    return num.toString();
  };
  
  return (
    <div className={`border rounded-lg p-3 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider opacity-70">{title}</p>
          <p className={`text-xl font-bold mt-1`}>
            {formatNumber(value)}
          </p>
          {trend !== undefined && (
            <p className={`text-xs mt-1 flex items-center gap-1 ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {trend >= 0 ? '+' : ''}{formatNumber(Math.abs(trend))} (24h)
            </p>
          )}
        </div>
        {Icon && <Icon className={`w-6 h-6 opacity-50`} />}
      </div>
    </div>
  );
};

const DDoSThreatMap = () => {
  const { theme, setTheme, isDark, themes: availableThemes } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cardSizes, setCardSizes] = useState({
    regions: 200,
    sectors: 200,
    intrusions: 200,
    malware: 280,
    vulnerabilities: 280,
    tools: 280,
  });
  const [viewState, setViewState] = useState({
    longitude: 30.5,
    latitude: 3.64,
    zoom: 1.8
  });

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/threat-intel/feeds`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const liveData = res.ok ? await res.json() : null;
      setData(generateThreatData(liveData));
      setLastUpdated(new Date());
    } catch {
      setData(generateThreatData(null));
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
            <Shield className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Threat Intelligence Dashboard</h1>
            <p className="text-muted-foreground text-sm">Real-time global threat monitoring</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            <Clock className="w-3 h-3 inline mr-1" />
            Updated: {lastUpdated.toLocaleTimeString()}
          </span>
          
          {/* Theme Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Palette className="w-4 h-4 mr-1" />
                Theme
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Select Theme</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setTheme('light')}>
                <Sun className="w-4 h-4 mr-2" /> Light
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme('dark')}>
                <Moon className="w-4 h-4 mr-2" /> Dark
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs">Star Wars Themes</DropdownMenuLabel>
              {Object.values(availableThemes || {}).filter(t => t.category === 'starwars').slice(0, 5).map(t => (
                <DropdownMenuItem key={t.id} onClick={() => setTheme(t.id)}>
                  <span className="mr-2">{t.icon}</span> {t.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Layout Settings */}
          <Dialog open={showSettings} onOpenChange={setShowSettings}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <LayoutGrid className="w-4 h-4 mr-1" />
                Layout
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Card Size Settings</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {Object.entries(cardSizes).map(([key, value]) => (
                  <div key={key} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="capitalize">{key}</Label>
                      <span className="text-sm text-muted-foreground">{value}px</span>
                    </div>
                    <Slider
                      value={[value]}
                      onValueChange={([v]) => setCardSizes(prev => ({ ...prev, [key]: v }))}
                      min={150}
                      max={500}
                      step={10}
                    />
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadData}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Top Metrics Row */}
      <div className="grid grid-cols-6 gap-3 mb-4">
        <MetricCard 
          title="Threat Actors" 
          value={data?.stats.threatActors} 
          trend={data?.trends.threatActors}
          icon={Users}
          color="cyan"
        />
        <MetricCard 
          title="Intrusion Sets" 
          value={data?.stats.intrusionSets} 
          trend={data?.trends.intrusionSets}
          icon={Target}
          color="orange"
        />
        <MetricCard 
          title="Campaigns" 
          value={data?.stats.campaigns} 
          trend={data?.trends.campaigns}
          icon={Activity}
          color="green"
        />
        <MetricCard 
          title="Malware" 
          value={data?.stats.malware} 
          trend={data?.trends.malware}
          icon={Bug}
          color="purple"
        />
        <MetricCard 
          title="Indicators" 
          value={data?.stats.indicators} 
          trend={data?.trends.indicators}
          icon={Eye}
          color="pink"
        />
        <MetricCard 
          title="Observables" 
          value={data?.stats.observables} 
          trend={data?.trends.observables}
          icon={Database}
          color="yellow"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-12 gap-4">
        {/* Left Column - Targeted Regions */}
        <div className="col-span-2 space-y-4">
          <ResizableCard title="TARGETED REGIONS" icon={Globe} defaultHeight={cardSizes.regions}>
            <div className="space-y-2">
              {data?.targetedRegions.map((region, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-xs truncate max-w-[80px]">{region.name}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${(region.count / data.targetedRegions[0].count) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-primary w-10 text-right font-mono">
                      {region.count >= 1000 ? `${(region.count/1000).toFixed(1)}K` : region.count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </ResizableCard>

          <ResizableCard title="TARGETED SECTORS" icon={Target} defaultHeight={cardSizes.sectors}>
            <div className="space-y-2">
              {data?.targetedSectors.slice(0, 8).map((sector, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-xs truncate max-w-[80px]">{sector.name}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-orange-500 rounded-full"
                        style={{ width: `${(sector.count / data.targetedSectors[0].count) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-orange-500 w-10 text-right font-mono">
                      {sector.count >= 1000 ? `${(sector.count/1000).toFixed(1)}K` : sector.count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </ResizableCard>
        </div>

        {/* Center - Map */}
        <div className="col-span-5">
          <Card className={`overflow-hidden ${mapFullscreen ? 'fixed inset-4 z-50' : ''}`}>
            <CardHeader className="py-2 px-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-primary" />
                  <CardTitle className="text-sm font-semibold">TARGETED COUNTRIES</CardTitle>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-6 w-6">
                    <Download className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setMapFullscreen(!mapFullscreen)}>
                    {mapFullscreen ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <style>{`
                .osm-dark-canvas .maplibregl-canvas {
                  filter: invert(1) hue-rotate(180deg) brightness(0.9) contrast(0.95) saturate(0.7);
                }
              `}</style>
              <div className={`${mapFullscreen ? 'h-[calc(100vh-100px)]' : 'h-[300px]'} ${isDark ? 'osm-dark-canvas' : ''}`}>
                <Map
                  {...viewState}
                  onMove={evt => setViewState(evt.viewState)}
                  mapStyle={isDark ? MAP_STYLE : MAP_STYLE_LIGHT}
                  attributionControl={false}
                >
                  <NavigationControl position="top-right" />
                  {data?.targetedCountries.slice(0, 15).map((country) => (
                    <Marker
                      key={country.code}
                      longitude={country.lng}
                      latitude={country.lat}
                      anchor="center"
                    >
                      <div 
                        className="relative cursor-pointer group"
                        onClick={() => setSelectedCountry(country)}
                      >
                        <div 
                          className="w-3 h-3 rounded-full bg-orange-500 animate-ping absolute"
                          style={{ opacity: Math.min(country.attacks / 1000, 0.8) }}
                        />
                        <div 
                          className="w-3 h-3 rounded-full bg-orange-500 relative"
                          style={{ 
                            transform: `scale(${Math.min(1 + country.attacks / 2000, 2.5)})`,
                            boxShadow: '0 0 10px rgba(249, 115, 22, 0.6)'
                          }}
                        />
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-popover text-popover-foreground text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap border shadow-lg z-10">
                          {country.name}: {country.attacks.toLocaleString()} attacks
                        </div>
                      </div>
                    </Marker>
                  ))}
                </Map>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Intrusion Sets & Malware */}
        <div className="col-span-2 space-y-4">
          <ResizableCard title="ACTIVE INTRUSION SETS" icon={AlertTriangle} defaultHeight={cardSizes.intrusions}>
            <div className="space-y-2">
              {data?.threatActors.map((actor, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-xs text-yellow-500 font-medium">{actor.id}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-14 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-yellow-500 rounded-full"
                        style={{ width: `${(actor.attacks / data.threatActors[0].attacks) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs w-8 text-right font-mono">{actor.attacks}</span>
                  </div>
                </div>
              ))}
            </div>
          </ResizableCard>

          <ResizableCard title="ACTIVE MALWARE" icon={Bug} defaultHeight={cardSizes.malware}>
            <DonutChart data={data?.malware || []} size={130} />
          </ResizableCard>
        </div>

        {/* Far Right - Vulnerabilities & Tools */}
        <div className="col-span-3 space-y-4">
          <ResizableCard title="ACTIVE VULNERABILITIES" icon={Shield} defaultHeight={cardSizes.vulnerabilities}>
            <div className="space-y-2">
              {data?.vulnerabilities.map((vuln, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-red-500" />
                    <span className="text-xs font-mono">{vuln.cve}</span>
                  </div>
                  <Badge variant="destructive" className="text-xs">
                    {vuln.count}
                  </Badge>
                </div>
              ))}
            </div>
          </ResizableCard>

          <ResizableCard title="ACTIVE TOOLS" icon={Zap} defaultHeight={cardSizes.tools}>
            <DonutChart data={data?.activeTools || []} size={130} />
          </ResizableCard>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-4 gap-4 mt-4">
        {/* Latest Campaigns */}
        <Card>
          <CardHeader className="py-2 px-3">
            <CardTitle className="text-sm font-semibold">LATEST CAMPAIGNS</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <div className="space-y-2">
              {data?.latestCampaigns.map((campaign, idx) => (
                <div key={idx} className="p-2 bg-muted/50 rounded">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-primary font-medium">{campaign.name}</span>
                    <Badge variant={campaign.severity === 'critical' ? 'destructive' : 'secondary'} className="text-xs">
                      {campaign.severity}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{campaign.target}</p>
                  <p className="text-xs text-muted-foreground/70">{campaign.date}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Indicator Sources */}
        <Card>
          <CardHeader className="py-2 px-3">
            <CardTitle className="text-sm font-semibold">INDICATORS SOURCES</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <div className="space-y-2">
              {data?.indicatorSources.map((source, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                  <div className="flex items-center gap-2">
                    <Database className="w-4 h-4 text-primary" />
                    <span className="text-xs">{source.name}</span>
                  </div>
                  <span className="text-xs text-primary font-mono">
                    {(source.count / 1000).toFixed(1)}K
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Countries Table */}
        <Card className="col-span-2">
          <CardHeader className="py-2 px-3">
            <CardTitle className="text-sm font-semibold">TOP TARGETED COUNTRIES</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <div className="grid grid-cols-2 gap-2">
              {data?.targetedCountries.slice(0, 10).map((country, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                  <span className="text-xs">{country.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-orange-500 font-mono">
                      {country.attacks >= 1000 ? `${(country.attacks/1000).toFixed(1)}K` : country.attacks}
                    </span>
                    {country.trend === 'up' ? (
                      <TrendingUp className="w-3 h-3 text-red-500" />
                    ) : (
                      <TrendingDown className="w-3 h-3 text-green-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Live Threat Feeds Section */}
      {data?.liveFeeds && (data.liveFeeds.urlhaus.length > 0 || data.liveFeeds.threatfox.length > 0 || data.liveFeeds.botnet_c2.length > 0) && (
        <div className="mt-4 space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <h2 className="text-sm font-bold uppercase tracking-wider">Live Open Source Threat Feeds</h2>
            {data.liveFeeds.lastUpdated && (
              <span className="text-xs text-muted-foreground ml-auto">
                Feed data: {new Date(data.liveFeeds.lastUpdated).toLocaleTimeString()}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Botnet C2 Servers */}
            {data.liveFeeds.botnet_c2.length > 0 && (
              <Card>
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full" /> BOTNET C2 SERVERS
                    <Badge variant="destructive" className="text-[10px] ml-auto">{data.liveFeeds.botnet_c2.length}</Badge>
                  </CardTitle>
                  <p className="text-[10px] text-muted-foreground">Source: abuse.ch Feodo Tracker</p>
                </CardHeader>
                <CardContent className="p-3 pt-0 max-h-48 overflow-y-auto">
                  <div className="space-y-1">
                    {data.liveFeeds.botnet_c2.slice(0, 10).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-1.5 bg-muted/50 rounded text-xs">
                        <span className="font-mono text-red-500">{item.ip}</span>
                        <span className="text-muted-foreground">{item.malware || item.type || 'C2'}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            {/* URLhaus Malicious URLs */}
            {data.liveFeeds.urlhaus.length > 0 && (
              <Card>
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <span className="w-2 h-2 bg-orange-500 rounded-full" /> MALICIOUS URLS
                    <Badge className="text-[10px] ml-auto bg-orange-500">{data.liveFeeds.urlhaus.length}</Badge>
                  </CardTitle>
                  <p className="text-[10px] text-muted-foreground">Source: abuse.ch URLhaus</p>
                </CardHeader>
                <CardContent className="p-3 pt-0 max-h-48 overflow-y-auto">
                  <div className="space-y-1">
                    {data.liveFeeds.urlhaus.slice(0, 10).map((item, idx) => (
                      <div key={idx} className="p-1.5 bg-muted/50 rounded text-xs">
                        <div className="font-mono truncate text-orange-500">{item.host || item.url}</div>
                        <div className="text-muted-foreground flex gap-2 mt-0.5">
                          <span>{item.threat || 'malware'}</span>
                          {item.tags?.length > 0 && <span>{item.tags.join(', ')}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            {/* ThreatFox IOCs */}
            {data.liveFeeds.threatfox.length > 0 && (
              <Card>
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <span className="w-2 h-2 bg-amber-500 rounded-full" /> THREAT INDICATORS
                    <Badge className="text-[10px] ml-auto bg-amber-500">{data.liveFeeds.threatfox.length}</Badge>
                  </CardTitle>
                  <p className="text-[10px] text-muted-foreground">Source: abuse.ch ThreatFox</p>
                </CardHeader>
                <CardContent className="p-3 pt-0 max-h-48 overflow-y-auto">
                  <div className="space-y-1">
                    {data.liveFeeds.threatfox.slice(0, 10).map((item, idx) => (
                      <div key={idx} className="p-1.5 bg-muted/50 rounded text-xs">
                        <div className="font-mono truncate">{item.ioc}</div>
                        <div className="text-muted-foreground flex gap-2 mt-0.5">
                          <span className="text-amber-500">{item.malware || item.threat_type}</span>
                          <span>{item.ioc_type}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DDoSThreatMap;
